import axios from "axios";
import { Message, Loading, MessageBox } from "element-ui";
import router from "@/router";
import store from "@/store";
import { tansParams } from "@/utils/index.js";
import website from "@/config/website";
import { gotoReLogin } from "@/utils/ncnp";

// 是否显示重新登录
let isRelogin = { show: false };
let downloadLoadingInstance;
// 获取当前环境
const env = process.env.NODE_ENV;
// console.log('当前环境:', env)
// console.log('API 地址:', process.env.VUE_APP_BASE_API)

// axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'
// axios.defaults.headers['tenant-id'] = '1'
// 创建 axios 实例
const service = axios.create({
    baseURL: process.env.NODE_ENV === "development" ? "/api" : "/api", // 开发环境使用代理
    timeout: 10000 * 6 * 10, // 请求超时时间
    withCredentials: false, // 修改为 false，避免跨域请求时的 cookie 问题
});

// 请求拦截器
service.interceptors.request.use(
    (config) => {
        // 在请求发送之前做一些处理

        // 处理 GET 请求参数映射
        if (config.method === "get" && config.params) {
            let url = config.url + "?" + tansParams(config.params);
            url = url.slice(0, -1);
            config.params = {};
            config.url = url;
        }

        // 从配置中获取是否需要登录
        const needLogin = config.headers.needLogin !== false;

        // 如果需要登录，添加 token 到请求头
        if (needLogin) {
            // Blade-Auth 头必须用 BladeX access_token（NCNP 登录时 createBladeToken 生成，
            // SET_ACCESS_TOKEN 写入 store accessToken getter / localStorage.accessToken），
            // 不能用 userToken——后者只是前端会话句柄（UUID），后端 BladeX 无法解析，
            // 会导致全部鉴权接口 401 并触发响应拦截器的重新登录循环。
            // 故此处不再回退 store.getters.token（根 getters 未定义 token，恒为 undefined，
            // 此前靠 || 兜底到 accessToken 才歪打正着工作）。
            const token = store.getters.accessToken || localStorage.getItem("accessToken");
            if (token) {
                // 设置请求头 Authorization
                config.headers["Blade-Auth"] = `bearer ${token}`;
                // config.headers['Blade-Auth'] = token
                config.headers["Tenant-Id"] = "000000";
                config.headers["Blade-Requested-With"] = "BladeHttpRequest";
                config.headers["Authorization"] =
                    "Basic c2FiZXlzOnNhYmVyM19zZWNYZXQ=";
            }
        }

        // 根据不同环境可以添加不同的处理逻辑
        if (process.env.NODE_ENV === "development") {
            // 开发环境特殊处理
            // console.log('开发环境请求:', config.url)
        } else if (process.env.VUE_APP_TITLE === "测试环境") {
            // 测试环境特殊处理
            // console.log('测试环境请求:', config.url)
        } else if (process.env.VUE_APP_TITLE === "生产环境") {
            // 生产环境特殊处理
            // 生产环境一般不打印日志
        }

        return config;
    },
    (error) => {
        // 处理请求错误
        console.log(error); // 用于调试
        return Promise.reject(error);
    }
);

// 响应拦截器
service.interceptors.response.use(
    (response) => {
        const res = response.data;

        // 如果返回的状态码不是 200，说明接口有问题，需要处理
        // 处理各种错误情况

        // 401: 未登录或 token 过期
        if (res.code === 401) {
            // 提示用户选择是否重新登录
            store.dispatch("user/clearUserInfo");
            if (!isRelogin.show) {
                isRelogin.show = true;
                MessageBox.confirm("登录状态已过期，是否重新登录？", "提示", {
                    confirmButtonText: "重新登录",
                    cancelButtonText: "返回",
                    type: "warning",
                })
                    .then(() => {
                        isRelogin.show = false;
                        // NCNP 用户走 welcome task_id=login，其余走 CA SSO
                        gotoReLogin();
                    })
                    .catch(() => {
                        // 跳转到首页
                        isRelogin.show = false;
                        router.push("/");
                    });
            }
        } else {
            return res;
        }

        // 其他错误情况
        Message({
            message: res.message || "请求失败",
            type: "error",
            duration: 5 * 1000,
        });

        return Promise.reject(new Error(res.message || "请求失败"));
    },
    (error) => {
        console.log("请求错误: " + error); // 用于调试

        // 处理 HTTP 错误状态码
        if (error.response) {
            switch (error.response.status) {
                case 401:
                    // 未授权，登录信息过期或未登录
                    store.dispatch("user/clearUserInfo");
                    if (!isRelogin.show) {
                        isRelogin.show = true;
                        MessageBox.confirm(
                            "登录状态已过期，是否重新登录？",
                            "提示",
                            {
                                confirmButtonText: "重新登录",
                                cancelButtonText: "返回",
                                type: "warning",
                            }
                        )
                            .then(() => {
                                isRelogin.show = false;
                                // NCNP 用户走 welcome task_id=login，其余走 CA SSO
                                gotoReLogin();
                            })
                            .catch(() => {
                                // 跳转到首页
                                isRelogin.show = false;
                                router.push("/");
                            });
                    }

                    break;

                case 403:
                    Message({
                        message: "没有权限访问该资源",
                        type: "error",
                        duration: 5 * 1000,
                    });
                    break;

                case 404:
                    Message({
                        message: "请求的资源不存在",
                        type: "error",
                        duration: 5 * 1000,
                    });
                    break;

                case 500:
                    Message({
                        message: "服务器内部错误",
                        type: "error",
                        duration: 5 * 1000,
                    });
                    break;

                default:
                    Message({
                        message: error.message || "请求失败",
                        type: "error",
                        duration: 5 * 1000,
                    });
            }
        } else {
            // 处理网络错误、超时等
            Message({
                message: "网络异常，请检查您的网络连接",
                type: "error",
                duration: 5 * 1000,
            });
        }

        return Promise.reject(error);
    }
);

// 封装 GET 请求
export function get(url, params, needLogin = false) {
    return service({
        url,
        method: "get",
        params,
        headers: {
            needLogin,
        },
    });
}

// 封装 POST 请求
export function post(url, data, needLogin = true) {
    return service({
        url,
        method: "post",
        data,
        headers: {
            needLogin,
        },
    });
}

// 通用下载方法
export function download(url, params, filename, config) {
    downloadLoadingInstance = Loading.service({
        text: "正在下载数据，请稍候",
        spinner: "el-icon-loading",
        background: "rgba(0, 0, 0, 0.7)",
    });
    return service
        .get(url, params, {
            // transformRequest: [(params) => { return tansParams(params) }],
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            responseType: "blob",
            ...config,
        })
        .then(async (data) => {
            const isBlob = blobValidate(data);
            if (isBlob) {
                const blob = new Blob([data]);
                saveAs(blob, filename);
            } else {
                const resText = await data.text();
                const rspObj = JSON.parse(resText);
                const errMsg =
                    errorCode[rspObj.code] ||
                    rspObj.msg ||
                    errorCode["default"];
                Message.error(errMsg);
            }
            downloadLoadingInstance.close();
        })
        .catch((r) => {
            console.error(r);
            Message.error("下载文件出现错误，请联系管理员！");
            downloadLoadingInstance.close();
        });
}

export default service;
